import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { lazy } from 'react'
import App from '@/App'
import { logger } from '@/utils/logger'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'

// Info pages
const Home = lazy(() => import('@/pages/info/Home'))
const About = lazy(() => import('@/pages/info/About'))
const Contact = lazy(() => import('@/pages/info/Contact'))
const Academy = lazy(() => import('@/pages/info/Academy'))
const FAQ = lazy(() => import('@/pages/info/FAQ'))
const ReportLocation = lazy(() => import('@/pages/info/ReportLocation'))
const Terms = lazy(() => import('@/pages/info/Terms'))
const PrivacyPolicy = lazy(() => import('@/pages/info/PrivacyPolicy'))
const NotFound = lazy(() => import('@/pages/info/NotFound'))

// Shop pages
const Menu = lazy(() => import('@/pages/shop/Menu'))
const ProductPage = lazy(() => import('@/pages/shop/ProductPage'))
const Cart = lazy(() => import('@/pages/shop/Cart'))
const Checkout = lazy(() => import('@/pages/shop/Checkout'))
const OrderTracking = lazy(() => import('@/pages/shop/OrderTracking'))
const KreatorLanding = lazy(() => import('@/pages/shop/Kreator/KreatorLanding'))
const KreatorFlow = lazy(() => import('@/pages/shop/Kreator/KreatorFlow'))


// Blog pages
const BlogList = lazy(() => import('@/pages/info/Blog/BlogList'))
const BlogPost = lazy(() => import('@/pages/info/Blog/BlogPost'))

// Auth pages
const Login = lazy(() => import('@/pages/auth/Login'))
const Register = lazy(() => import('@/pages/auth/Register'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'))
const AccessDenied = lazy(() => import('@/pages/auth/AccessDenied'))

// Account pages
const CustomerPanel = lazy(() => import('@/pages/account/CustomerPanel'))
const EmployeePanel = lazy(() => import('@/pages/account/EmployeePanel'))
const OrderSuccess = lazy(() => import('@/pages/account/OrderSuccess'))

// Manage pages (Moderator + Admin panels)
const ModeratorPanel = lazy(() => import('@/pages/manage/moderator/ModeratorPanel'))
const AdminPanel = lazy(() => import('@/pages/manage/admin/AdminPanel'))
const KitchenPanel = lazy(() => import('@/pages/manage/kitchen/KitchenPanel'))
const KitchenManager = lazy(() => import('@/pages/manage/kitchen/KitchenManager'))
const DeliveryPanel = lazy(() => import('@/pages/manage/delivery/DeliveryPanel'))
const StaffPanel = lazy(() => import('@/pages/manage/staff/StaffPanel'))
const VehiclesPanel = lazy(() => import('@/pages/manage/vehicles/VehiclesPanel'))
const AvailabilityPage = lazy(() => import('@/pages/staff/AvailabilityPage'))
const EmployeeManagement = lazy(() => import('@/pages/manage/employees/EmployeesPage'))

// Other pages
const Analytics = lazy(() => import('@/pages/Analytics'))
const FeedbackPage = lazy(() => import('@/pages/shop/Feedback'))
const RcpPage = lazy(() => import('@/pages/timekeeping/RcpPage'))

// Help pages
const KitchenHelp = lazy(() => import('@/pages/manage/help/KitchenHelp'))

const router = createBrowserRouter([
    {
        path: '/',
        element: <App />,
        children: [
            {
                index: true,
                element: <Home />,
            },
            {
                path: 'menu',
                children: [
                    { index: true, element: <Menu /> },
                    { path: ':category', element: <Menu /> },
                ]
            },
            {
                path: 'produkt/:slug',
                element: <ProductPage />,
            },
            {
                path: 'koszyk',
                element: <Cart />,
            },
            {
                path: 'sledzenie/:trackingToken',
                element: <OrderTracking />,
            },
            {
                path: 'zamowienie',
                children: [
                    { index: true, element: <Checkout /> },
                    { path: 'sukces', element: <OrderSuccess /> },
                ]
            },
            {
                path: 'login',
                element: <Login />,
            },
            {
                path: 'rejestracja',
                element: <Register />,
            },
            {
                path: 'zapomniane-haslo',
                element: <ForgotPassword />,
            },
            {
                path: 'reset-password',
                element: <ResetPassword />,
            },
            {
                path: 'access-denied',
                element: <AccessDenied />,
            },
            {
                path: 'o-nas',
                element: <About />,
            },
            {
                path: 'akademia',
                element: <Academy />,
            },
            {
                path: 'kreator',
                children: [
                    { index: true, element: <KreatorLanding /> },
                    { path: ':categorySlug', element: <KreatorFlow /> },

                ],
            },
            {
                path: 'kontakt',
                element: <Contact />,
            },
            {
                path: 'zglos-lokalizacje',
                element: <ReportLocation />,
            },
            {
                path: 'blog',
                children: [
                    { index: true, element: <BlogList /> },
                    { path: ':slug', element: <BlogPost /> },
                ]
            },
            {
                path: 'faq',
                element: <FAQ />,
            },
            {
                path: 'moje-konto',
                element: <CustomerPanel />,
            },
            {
                path: 'panel-pracownika',
                element: <EmployeePanel />,
            },
            {
                path: 'analytics',
                element: <Analytics />,
            },
            {
                path: 'regulamin',
                element: <Terms />,
            },
            {
                path: 'polityka-prywatnosci',
                element: <PrivacyPolicy />,
            },
            {
                path: 'moderator/*',
                element: <ProtectedRoute requiredRoles={['moderator']}><ModeratorPanel /></ProtectedRoute>,
            },
            {
                path: 'manage',
                element: <Navigate to="/rcp" replace />,
            },
            {
                path: 'admin/*',
                element: <ProtectedRoute requiredRoles={['admin']}><AdminPanel /></ProtectedRoute>,
            },
            {
                path: 'kuchnia',
                element: <KitchenPanel />,
                children: [
                    { index: true, element: <Navigate to="zimna" replace /> },
                    { path: 'zimna', element: <KitchenManager mode="cold" /> },
                    { path: 'ciepla', element: <KitchenManager mode="hot" /> },
                    { path: 'pomoc', element: <KitchenHelp /> }
                ]
            },
            {
                path: 'dostawa/*',
                element: <DeliveryPanel />,
            },
            {
                path: 'manage/kadry/*',
                element: <ProtectedRoute requiredRoles={['kadrowa', 'grafik']}><StaffPanel /></ProtectedRoute>,
            },
            {
                path: 'manage/pojazdy/*',
                element: <VehiclesPanel />,
            },
            {
                path: 'manage/admin/employees/*',
                element: <ProtectedRoute requiredRoles={['ksiegowa']}><EmployeeManagement /></ProtectedRoute>,
            },
            {
                path: 'kadry',
                element: <Navigate to="/manage/kadry" replace />,
            },
            {
                path: 'dyspozycyjnosc',
                element: <AvailabilityPage />,
            },
            {
                path: 'ankieta/:token',
                element: <FeedbackPage />,
            },
            {
                path: 'rcp',
                element: <RcpPage />,
            },
            {
                path: 'rcp/system',
                element: <Navigate to="/rcp" replace />,
            },
            {
                path: '*',
                element: <NotFound />,
                loader: ({ request }) => {
                    logger.addBreadcrumb({ category: 'navigation', message: '404 Hit', data: { url: request.url }, level: 'warning' })
                    return null
                }
            }
        ],
    },
])

export const AppRouter = () => {
    return <RouterProvider router={router} />
}
